import { createReducer, on } from '@ngrx/store';
import { Option } from '@sapphire/result';

import {
  ADVANCED_FILTER_TYPE,
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  INITIAL_LOCATION_UPLOAD_STATE,
  supportsSurveyTags,
  SurveyKind,
} from '@ps/survey-utils';

import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
  SurveyCreationMcApiActions,
  SurveyCreationPreDraftActions,
  SurveyCreationTrackerApiActions,
} from './survey-creation-audience.actions';
import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from '../insights/survey-creation-insights.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from '../launch/survey-creation-launch.actions';
import { hasLocaleChanged } from './utils/locale-changes.helper';
import {
  buildMcPreDraftChildren,
  buildTrackerPreDraftChildren,
  initializeAudienceState,
  isLoadedAudienceSibling,
  omitChild,
  patchChildren,
  preserveChildLocale,
  resolveActiveChildKey,
} from './utils/audience-children.helper';
import {
  CHILD_SCOPE,
  collapseToDefaultChild,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';
import { surveyTagScope } from './utils/survey-tags-changes.helper';
import {
  buildLoadedChild,
  hydrateChildFromSurvey,
  keyedLoadedChildren,
} from './domain/build-audience-form-state-from-survey';
import { hydrateLocationGroups } from './domain/location-groups-hydration';
import { hasRemovedUploadedLocation } from './domain/uploaded-location-removal';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  // Cross-listens to both stage-specific init actions — the save pipeline
  // reads audience child state regardless of which page opened it. See
  // SurveyCreationAudienceActions.init's doc comment.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, action): SurveyCreationAudienceState => {
      const { surveyId } = action;
      const { rootSurveyId } = coreContextOf(action);

      const alreadyTargeted = !!surveyId && rootSurveyId === surveyId;

      if (alreadyTargeted) {
        return state;
      }

      // A locale switch re-`init`s with a sibling's id. Mirrors the same
      // guard in core's init handler - resetting would wipe every
      // sibling's already-loaded form state back to defaults.
      if (!!surveyId && isLoadedAudienceSibling(state.children, surveyId)) {
        return state;
      }

      return initializeAudienceState(surveyId);
    },
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state, action): SurveyCreationAudienceState => ({
      ...state,
      children: delegateRecordToDefaultChild(
        state.children,
        coreContextOf(action).activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY,
      ),
    }),
  ),

  on(
    SurveyCreationCoreActions.resetTrackerConfig,
    (state): SurveyCreationAudienceState => ({
      ...state,
      children: collapseToDefaultChild(state.children),
    }),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, action): SurveyCreationAudienceState => ({
      ...state,
      children: {
        ...state.children,
        ...keyedLoadedChildren(
          action.surveys.filter(({ id }) => !state.children[id]),
        ),
      },
    }),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadSpecificAudienceParentSuccess,
    (state, { parentId, qualifications }): SurveyCreationAudienceState => ({
      ...state,
      children: {
        ...state.children,
        [parentId]: {
          ...DEFAULT_CHILD_AUDIENCE_STATE,
          qualifications: {
            value: qualifications,
            valid: true,
            errors: [],
            isDirty: false,
          },
        },
      },
    }),
  ),

  // The added/removed locale's registry row (survey, error, participant) is
  // the `core` slice's business; audience only owns the form state.
  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    SurveyCreationTrackerApiActions.addWaveSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      children: {
        ...state.children,
        [survey.id]: buildLoadedChild(survey),
      },
    }),
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationAudienceState => ({
      ...state,
      children: omitChild(state.children, surveyId),
    }),
  ),

  on(
    SurveyCreationTrackerApiActions.deleteWaveSuccess,
    (state, { surveyId }): SurveyCreationAudienceState => ({
      ...state,
      children: omitChild(state.children, surveyId),
    }),
  ),

  // Pre-draft only: before a surveyId exists, each locale gets its own
  // child. Once rootSurveyId is set, the survey is real and its locale is fixed.
  on(
    SurveyCreationPreDraftActions.buildPreDraftChildren,
    (state, action): SurveyCreationAudienceState => {
      if (coreContextOf(action).rootSurveyId) {
        return state;
      }

      const { numberOfWaves } = coreContextOf(action).trackerConfig ?? {};

      return {
        ...state,
        children: numberOfWaves
          ? buildTrackerPreDraftChildren(
              state.children,
              numberOfWaves,
              action.localeData,
            )
          : buildMcPreDraftChildren(state.children, action.localeData),
      };
    },
  ),

  // Hydrate the in-memory survey from the API response, and mark the linked-tags
  // follow-up request (loadSurveyTagsForEdit$) as in flight — unless the survey
  // has no tags of its own, in which case that request never goes out.
  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, action): SurveyCreationAudienceState => {
      const { survey } = action;
      return patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.keys([survey.id]),
        (rootSurvey) => ({
          ...hydrateChildFromSurvey(survey),
          tagLinks: {
            ...rootSurvey.tagLinks,
            loadingLinkedTags: supportsSurveyTags(survey),
          },
        }),
      );
    },
  ),

  // Seeds the active child's own form state (a project's active child differs
  // from the root just hydrated above).
  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        hydrateChildFromSurvey(action.survey),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.loadLocationGroupsSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.keys([action.surveyId]),
        (child) => hydrateLocationGroups(child, action).unwrapOr({}),
      ),
  ),

  on(
    SurveyCreationAudienceActions.updateMargin,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        margin: action.margin,
      }),
  ),

  // Cross-listens to an insights action it does not own: the margin form is
  // hydrated from the saved survey, and a save sends its Buyer CPI over the
  // applied offer price. The Target Margin row is not always on screen to
  // re-seed it, so the form follows the applied price here — a cleared override
  // included: a stale Buyer CPI would go out as `offer_price` and the backend
  // would re-apply the override the suppliers PUT just removed.
  on(
    SurveyCreationInsightsApiActions.syncGlobalPriceOverride,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, (child) =>
        Option.from(child.margin.value).mapOr({}, (margin) => ({
          margin: {
            ...child.margin,
            value: { ...margin, buyerCpi: action.offerPrice },
          },
        })),
      ),
  ),

  // Cross-listens to an insights action it does not own: a confirmed Buyer CPI is
  // the survey's offer price, so the navbar CPI and the supplier panel follow it.
  // Setting it by hand takes pricing control from system-managed PurePrice, as
  // the CPI drawer's Apply does.
  on(
    SurveyCreationInsightsActions.applyBuyerCpi,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            priceOverride: action.offerPrice,
            defaultPurePriceOverride: false,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.queueHighCpiConfirmation,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pendingHighCpiConfirmations: [
            ...child.pendingHighCpiConfirmations,
            action.cpi,
          ],
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.clearHighCpiConfirmationQueue,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        pendingHighCpiConfirmations: [],
      }),
  ),

  on(
    SurveyCreationAudienceActions.updateMetadata,
    (state, action): SurveyCreationAudienceState => {
      const { metadata } = action;
      const isMultiLocale = Object.keys(state.children).length > 1;

      return patchChildren(
        state,
        coreContextOf(action),
        action.scope,
        (child) => {
          // Country/language changes go through the locale-change flow, not here.
          const childMetadata = isMultiLocale
            ? preserveChildLocale(metadata, child.metadata)
            : metadata;
          return {
            metadata: childMetadata,
            advancedFeasibilityFilters: hasLocaleChanged(
              child.metadata,
              childMetadata,
            )
              ? { ...EMPTY_ADVANCED_FEASIBILITY_FILTERS }
              : child.advancedFeasibilityFilters,
          };
        },
      );
    },
  ),

  on(
    SurveyCreationChildrenActions.patchChildMetadata,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), action.scope, (child) => ({
        metadata: {
          ...child.metadata,
          value: child.metadata.value && {
            ...child.metadata.value,
            ...action.changes,
          },
          isDirty: true,
        },
      })),
  ),

  on(
    SurveyCreationAudienceActions.updateQualifications,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), action.scope, (child) => ({
        qualifications: action.qualifications,
        // An uploaded location's file goes with its qualification, whichever change removed it.
        ...(hasRemovedUploadedLocation(child, action.qualifications.value)
          ? { locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE } }
          : {}),
      })),
  ),

  on(
    SurveyCreationAudienceActions.updateQuotas,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), action.scope, {
        quotas: action.quotas,
      }),
  ),

  on(
    SurveyCreationAudienceActions.uploadLocationCodes,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        locationFileUpload: {
          qualificationCode: action.qualificationCode,
          loading: true,
          error: null,
          filePath: null,
          result: null,
        },
      }),
  ),

  on(
    SurveyCreationAudienceApiActions.uploadLocationCodesSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        locationFileUpload: {
          qualificationCode: action.qualificationCode,
          loading: false,
          error: null,
          filePath: action.result.filePath,
          result: action.result,
        },
      }),
  ),

  on(
    SurveyCreationAudienceApiActions.uploadLocationCodesFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        locationFileUpload: {
          qualificationCode: action.qualificationCode,
          loading: false,
          error: action.error,
          filePath: null,
          result: null,
        },
      }),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        advancedFeasibilityFilters: action.filters,
      }),
  ),

  on(
    SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          // A link file only exists in Include mode, and mapToRefList only
          // sends the links for an Include filter — so make that explicit.
          advancedFeasibilityFilters: {
            ...child.advancedFeasibilityFilters,
            type: ADVANCED_FILTER_TYPE.INCLUDE,
            psidLinks: action.result.links,
          },
        }),
      ),
  ),

  // Clearing the respondent list wipes it server-side too, so the uploaded
  // link rows go with it.
  on(
    SurveyCreationLaunchActions.clearUniqueLinkPsidFile,
    SurveyCreationLaunchApiActions.clearRespondentListSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          advancedFeasibilityFilters: {
            ...child.advancedFeasibilityFilters,
            psidLinks: [],
          },
        }),
      ),
  ),

  // Same state the drawer commits, but without the `applyAdvancedFilters`
  // trigger — the launch page never displays feasibility, so resolving the
  // respondent filters must not start recalculations.
  on(
    SurveyCreationLaunchApiActions.resolveRespondentFiltersSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        advancedFeasibilityFilters: action.filters,
      }),
  ),

  on(
    SurveyCreationLaunchActions.restoreSavedGrouping,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          advancedFeasibilityFilters: {
            ...child.advancedFeasibilityFilters,
            dateRange: { start: null, end: null },
            matchedGroupIds: [],
            manualGroupIds: action.savedSurveyIds,
            psidRefs: [],
          },
        }),
      ),
  ),

  // Like the resolved respondent filters above: same state, no recalculation.
  on(
    SurveyCreationLaunchActions.updateRespondentStatuses,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          advancedFeasibilityFilters: {
            ...child.advancedFeasibilityFilters,
            respondentStatuses: action.statuses,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveySuccess,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
    (state, action): SurveyCreationAudienceState => {
      const { survey } = action;
      const { activeChildKey } = coreContextOf(action);
      const oldChild =
        state.children[resolveActiveChildKey(state, activeChildKey)];

      return {
        children: {
          [survey.id]: { ...oldChild },
        },
      };
    },
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        SurveyKind.isGroupMc(action.survey)
          ? CHILD_SCOPE.ALL_PARTICIPANTS
          : CHILD_SCOPE.ACTIVE,
        (child) => ({
          metadata: { ...child.metadata, isDirty: false },
          margin: { ...child.margin, isDirty: false },
          qualifications: { ...child.qualifications, isDirty: false },
          quotas: { ...child.quotas, isDirty: false },
        }),
      ),
  ),

  // A sibling is saved for its targeting alone, so only that is now clean.
  on(
    SurveyCreationCoreApiActions.saveSiblingSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState =>
      patchChildren(
        state,
        { activeChildKey: null, participantKeys: [] },
        CHILD_SCOPE.keys([survey.id]),
        (child) => ({
          qualifications: { ...child.qualifications, isDirty: false },
          quotas: { ...child.quotas, isDirty: false },
        }),
      ),
  ),

  // No cloneSurveySuccess handler: navigateToEditSurvey$ dispatches reset()
  // right after it, so anything written here would be wiped before it is read.

  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        surveyTagScope(coreContextOf(action).kind),
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: action.tagIds,
            loadingLinkedTags: false,
            selectedTagIds: action.tagIds,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: [],
            loadingLinkedTags: false,
            selectedTagIds: [],
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.selectTags,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: { ...child.tagLinks, selectedTagIds: action.tagIds },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.linkSurveyTagsSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: action.tagIds,
            linkTagsError: null,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.linkSurveyTagsFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: { ...child.tagLinks, linkTagsError: action.error },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.setSuppliersMetaDataBlendId,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            blendId: action.blendId,
          },
        }),
      ),
  ),

  // Partial merge — the caller sends only the facts it changed (priceOverride
  // from the auto PurePrice recompute, defaultPurePriceOverride when the user
  // takes manual pricing control); every other field is left untouched.
  on(
    SurveyCreationAudienceActions.setSuppliersMetaData,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            ...action.metaData,
          },
        }),
      ),
  ),

  // Keeps the navbar's `priceOverride` read in sync with inline CPI edits.
  // Keyed by the edited survey's own id, not `CHILD_SCOPE.ACTIVE` — the
  // edited row isn't necessarily the active child.
  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.keys([action.survey.id]),
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            priceOverride: action.survey.offerPrice ?? null,
          },
        }),
      ),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
